<template>
  <div class="driver-invoice-detail">
    <!-- Loading -->
    <b-card v-if="loading">
      <div class="text-center py-5">
        <b-spinner variant="primary" />
        <p class="mt-2 text-muted">
          Chargement de la facture...
        </p>
      </div>
    </b-card>

    <!-- Not Found -->
    <b-card v-else-if="!invoice">
      <div class="text-center py-5">
        <feather-icon
          icon="AlertCircleIcon"
          size="48"
          class="text-danger"
        />
        <p class="mt-2 text-muted">
          Facture non trouvée
        </p>
        <b-button
          variant="primary"
          :to="{ name: 'driver-invoice-list' }"
        >
          Retour à la liste
        </b-button>
      </div>
    </b-card>

    <template v-else>
      <!-- Header -->
      <b-card class="mb-2">
        <div class="d-flex justify-content-between align-items-start flex-wrap">
          <div>
            <h3 class="mb-1">
              <feather-icon
                icon="FileTextIcon"
                size="22"
                class="mr-50"
              />
              {{ invoice.invoice_number }}
            </h3>
            <p class="text-muted mb-0">
              <small>Réf: {{ invoice.reference }} · Créée le {{ formatDate(invoice.created_at) }}</small>
            </p>
          </div>
          <div class="d-flex align-items-center" style="gap: 0.5rem">
            <b-badge
              :variant="statusVariant(invoice.status)"
              class="px-3 py-2"
              style="font-size: 0.85rem"
            >
              {{ statusLabel(invoice.status) }}
            </b-badge>

            <!-- Admin Actions -->
            <b-dropdown
              v-if="isAdmin && invoice.status !== 'paid' && invoice.status !== 'cancelled'"
              size="sm"
              variant="outline-primary"
              right
              no-caret
            >
              <template #button-content>
                <feather-icon
                  icon="SettingsIcon"
                  size="14"
                />
                Actions
              </template>
              <b-dropdown-item
                v-if="invoice.status === 'draft'"
                @click="updateStatus('confirmed')"
              >
                <feather-icon
                  icon="CheckCircleIcon"
                  size="14"
                  class="mr-50 text-info"
                />
                Confirmer
              </b-dropdown-item>
              <b-dropdown-item
                v-if="invoice.status === 'confirmed'"
                @click="updateStatus('paid')"
              >
                <feather-icon
                  icon="DollarSignIcon"
                  size="14"
                  class="mr-50 text-success"
                />
                Marquer payée
              </b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-item @click="cancelInvoice">
                <feather-icon
                  icon="XCircleIcon"
                  size="14"
                  class="mr-50 text-warning"
                />
                Annuler
              </b-dropdown-item>
              <b-dropdown-item
                v-if="invoice.status === 'draft'"
                @click="deleteInvoice"
              >
                <feather-icon
                  icon="TrashIcon"
                  size="14"
                  class="mr-50 text-danger"
                />
                Supprimer
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
      </b-card>

      <!-- Summary Cards -->
      <b-row class="mb-2">
        <!-- Driver Info -->
        <b-col
          lg="4"
          md="6"
          class="mb-2"
        >
          <b-card class="h-100">
            <h5 class="mb-3">
              <feather-icon
                icon="UserIcon"
                size="18"
                class="mr-50"
              />
              Livreur
            </h5>
            <div class="detail-row">
              <span class="label">Nom</span>
              <span class="value">{{ invoice.driver_name || invoice.driver_username || '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="label">Rôle</span>
              <span class="value">{{ invoice.driver_role || '-' }}</span>
            </div>
          </b-card>
        </b-col>

        <!-- Period Info -->
        <b-col
          lg="4"
          md="6"
          class="mb-2"
        >
          <b-card class="h-100">
            <h5 class="mb-3">
              <feather-icon
                icon="CalendarIcon"
                size="18"
                class="mr-50"
              />
              Période
            </h5>
            <div class="detail-row">
              <span class="label">Du</span>
              <span class="value">{{ formatDate(invoice.start_date) }}</span>
            </div>
            <div class="detail-row">
              <span class="label">Au</span>
              <span class="value">{{ formatDate(invoice.end_date) }}</span>
            </div>
          </b-card>
        </b-col>

        <!-- Financial Summary -->
        <b-col
          lg="4"
          md="12"
          class="mb-2"
        >
          <b-card class="h-100 summary-card">
            <h5 class="mb-3">
              <feather-icon
                icon="DollarSignIcon"
                size="18"
                class="mr-50"
              />
              Résumé financier
            </h5>
            <div
              v-if="invoice.total_deliveries > 0"
              class="detail-row"
            >
              <span class="label">Livraisons</span>
              <span class="value">{{ invoice.total_deliveries }} × {{ formatAmount(invoice.delivery_commission_rate) }} DT</span>
            </div>
            <div
              v-if="invoice.total_deliveries > 0"
              class="detail-row"
            >
              <span class="label">Commission livraison</span>
              <span class="value text-success">{{ formatAmount(invoice.delivery_commission_amount) }} DT</span>
            </div>
            <div
              v-if="invoice.total_pickups > 0"
              class="detail-row"
            >
              <span class="label">Ramassages</span>
              <span class="value">{{ invoice.total_pickups }} × {{ formatAmount(invoice.pickup_commission_rate) }} DT</span>
            </div>
            <div
              v-if="invoice.total_pickups > 0"
              class="detail-row"
            >
              <span class="label">Commission ramassage</span>
              <span class="value text-success">{{ formatAmount(invoice.pickup_commission_amount) }} DT</span>
            </div>
            <hr class="my-1">
            <div class="detail-row">
              <span class="label font-weight-bold">Total</span>
              <span class="value text-success font-weight-bold" style="font-size: 1.1rem">
                {{ formatAmount(invoice.total_amount) }} DT
              </span>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Delivery Items -->
      <b-card
        v-if="invoice.delivery_items && invoice.delivery_items.length > 0"
        class="mb-2"
      >
        <h5 class="mb-3">
          <feather-icon
            icon="PackageIcon"
            size="18"
            class="mr-50"
          />
          Colis livrés ({{ invoice.delivery_items.length }})
        </h5>
        <b-table
          :items="invoice.delivery_items"
          :fields="deliveryFields"
          responsive
          hover
          small
          striped
        >
          <template #cell(order_id)="data">
            <span class="font-weight-bold">{{ data.item.order_id || data.item.order }}</span>
          </template>
          <template #cell(order_status_display)="data">
            <b-badge :variant="data.item.order_status === 4 ? 'success' : 'warning'">
              {{ data.item.order_status_display || (data.item.order_status === 4 ? 'Livré' : 'Retour définitif') }}
            </b-badge>
          </template>
          <template #cell(is_paid)="data">
            <b-badge :variant="data.item.is_paid ? 'success' : 'danger'">
              {{ data.item.is_paid ? 'Oui' : 'Non' }}
            </b-badge>
          </template>
          <template #cell(commission_amount)="data">
            <span class="text-success font-weight-bold">
              {{ formatAmount(data.item.commission_amount) }} DT
            </span>
          </template>
        </b-table>
      </b-card>

      <!-- Pickup Items -->
      <b-card
        v-if="invoice.pickup_items && invoice.pickup_items.length > 0"
        class="mb-2"
      >
        <h5 class="mb-3">
          <feather-icon
            icon="ShoppingCartIcon"
            size="18"
            class="mr-50"
          />
          Ramassages ({{ invoice.pickup_items.length }})
        </h5>
        <b-table
          :items="invoice.pickup_items"
          :fields="pickupFields"
          responsive
          hover
          small
          striped
        >
          <template #cell(commission_amount)="data">
            <span class="text-success font-weight-bold">
              {{ formatAmount(data.item.commission_amount) }} DT
            </span>
          </template>
        </b-table>
      </b-card>

      <!-- Back Button -->
      <div class="mt-2">
        <b-button
          variant="outline-secondary"
          :to="{ name: 'driver-invoice-list' }"
        >
          <feather-icon
            icon="ArrowLeftIcon"
            size="14"
            class="mr-50"
          />
          Retour à la liste
        </b-button>
      </div>
    </template>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BButton,
  BSpinner,
  BBadge,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
} from 'bootstrap-vue'
import driverInvoiceApi from '@/libs/driverInvoiceApi'

export default {
  name: 'DriverInvoiceDetail',
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BButton,
    BSpinner,
    BBadge,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
  },
  data() {
    return {
      loading: false,
      invoice: null,
      deliveryFields: [
        { key: 'order_id', label: 'N° Colis' },
        { key: 'order_name', label: 'Nom' },
        { key: 'order_supplier_name', label: 'Expéditeur' },
        { key: 'completion_date', label: 'Date', formatter: this.formatDate },
        { key: 'order_status_display', label: 'Statut', class: 'text-center' },
        { key: 'is_paid', label: 'Payé', class: 'text-center' },
        { key: 'commission_amount', label: 'Commission', class: 'text-right' },
      ],
      pickupFields: [
        { key: 'pickup_id', label: 'N° Ramassage' },
        { key: 'pickup_supplier_name', label: 'Expéditeur' },
        { key: 'completion_date', label: 'Date', formatter: this.formatDate },
        { key: 'order_count', label: 'Colis', class: 'text-center' },
        { key: 'commission_amount', label: 'Commission', class: 'text-right' },
      ],
    }
  },
  computed: {
    isAdmin() {
      const role = this.$store.state.user?.role || this.$store.state.role || localStorage.getItem('role')
      return ['admin', 'superadmin', 'admin_developer', 'accountant'].includes(role)
    },
  },
  mounted() {
    this.loadInvoice()
  },
  methods: {
    async loadInvoice() {
      this.loading = true
      try {
        const response = await driverInvoiceApi.get(this.$route.params.id)
        this.invoice = response.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading invoice:', error)
        this.toast('Erreur lors du chargement de la facture', 'danger')
      } finally {
        this.loading = false
      }
    },

    async updateStatus(newStatus) {
      const labels = { confirmed: 'confirmer', paid: 'marquer comme payée' }
      const ok = await this.$bvModal.msgBoxConfirm(
        `Voulez-vous ${labels[newStatus]} cette facture ?`,
        {
          title: 'Confirmation',
          okVariant: newStatus === 'paid' ? 'success' : 'primary',
          okTitle: 'Oui',
          cancelTitle: 'Non',
        },
      )
      if (!ok) return
      try {
        const response = await driverInvoiceApi.updateStatus(this.invoice.id, newStatus)
        this.invoice = { ...this.invoice, ...response.data }
        this.toast('Statut mis à jour', 'success')
      } catch (error) {
        const msg = error.response?.data?.error || 'Erreur lors de la mise à jour'
        this.toast(msg, 'danger')
      }
    },

    async cancelInvoice() {
      const ok = await this.$bvModal.msgBoxConfirm(
        'Voulez-vous annuler cette facture ? Les colis redeviendront éligibles.',
        {
          title: 'Annuler la facture',
          okVariant: 'warning',
          okTitle: 'Oui, annuler',
          cancelTitle: 'Non',
        },
      )
      if (!ok) return
      try {
        const response = await driverInvoiceApi.cancel(this.invoice.id)
        this.invoice = { ...this.invoice, ...response.data }
        this.toast('Facture annulée', 'success')
      } catch (error) {
        const msg = error.response?.data?.error || 'Erreur lors de l\'annulation'
        this.toast(msg, 'danger')
      }
    },

    async deleteInvoice() {
      const ok = await this.$bvModal.msgBoxConfirm(
        'Voulez-vous supprimer définitivement cette facture ?',
        {
          title: 'Supprimer la facture',
          okVariant: 'danger',
          okTitle: 'Supprimer',
          cancelTitle: 'Annuler',
        },
      )
      if (!ok) return
      try {
        await driverInvoiceApi.delete(this.invoice.id)
        this.toast('Facture supprimée', 'success')
        this.$router.push({ name: 'driver-invoice-list' })
      } catch (error) {
        const msg = error.response?.data?.error || 'Erreur lors de la suppression'
        this.toast(msg, 'danger')
      }
    },

    statusVariant(status) {
      const map = {
        draft: 'secondary',
        confirmed: 'info',
        paid: 'success',
        cancelled: 'danger',
      }
      return map[status] || 'secondary'
    },

    statusLabel(status) {
      const map = {
        draft: 'Brouillon',
        confirmed: 'Confirmée',
        paid: 'Payée',
        cancelled: 'Annulée',
      }
      return map[status] || status
    },

    formatAmount(value) {
      if (value === null || value === undefined) return '0.000'
      return parseFloat(value).toFixed(3)
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    },

    toast(message, variant) {
      this.$bvToast.toast(message, {
        title: variant === 'success' ? 'Succès' : 'Erreur',
        variant,
        solid: true,
        autoHideDelay: 4000,
      })
    },
  },
}
</script>

<style scoped>
.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-row .label {
  color: #6c757d;
  font-size: 0.9rem;
}

.detail-row .value {
  font-weight: 500;
  font-size: 0.9rem;
}

.summary-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
}
</style>
